/**
 * Scotland Yard service layer — Firestore adapter for the pure SY engine.
 *
 * Responsibilities:
 *  - Compose the public state subdoc (`rooms/{code}/state/main`) and Mr. X's
 *    private subdoc (`rooms/{code}/private/mrx`) into the engine's input
 *    format, run `applyMove`, and write the result back atomically.
 *  - Drive a per-player "merged state" subscription that the platform's
 *    PlayerShell hands to `PlayerGameView` as TState. For Mr. X the merged
 *    state contains his real `mrxStation`; for everyone else it's `-1`.
 *
 * Mr. X privacy data flow:
 *  - On game start: `HostLobby.onStart` calls `gameModule.setupGame(room)` to
 *    get `{ public, mrxStation }`, then calls platform `startGame` with
 *    `{ gameStateDoc: public, privateDocs: { mrx: { station: mrxStation } } }`.
 *  - On per-client read: the SY module exposes `subscribePlayerState` which
 *    PlayerShell uses; this function subscribes the public state doc for
 *    everyone, AND additionally subscribes the private/mrx subdoc *only* when
 *    `uid === public.mrx.uid`. Detective clients never read the private doc
 *    so the network egress itself doesn't leak the value.
 *  - On move dispatch: `applyMove` runs in a Firestore transaction that
 *    reads both public + private docs, runs the engine, and writes both back.
 *    Mr. X's new station never lands in the public doc.
 */

import {
  doc,
  onSnapshot,
  runTransaction,
  type Firestore,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore'
import { getDb } from '@/platform/firebase/client'
import {
  dbdRoom,
  dbdRoomPrivate,
  dbdRoomState,
  applyMove as engineApplyMove,
  defaultAdjacency,
  EngineError,
} from '@mdev/common'
import type { Move, GameState } from '@mdev/common'
import {
  GameNotActiveError,
  IllegalMoveError,
  MrxStateMissingError,
  RoomNotInGameError,
} from '../lobby/errors'
import type { ScotlandYardState } from './module'

const db = getDb()

// --- Mr. X private doc shape ----------------------------------------------

/**
 * Body of `rooms/{code}/private/mrx`.
 *
 * `stationHistory` was added alongside the end-game replay feature. Older
 * games written before that change will not have the field; readers should
 * treat a missing value as `[station]` (just the current station).
 */
export interface MrxPrivateDoc {
  station: number
  stationHistory?: number[]
}

const MRX_KEY = 'mrx'

/** Sentinel returned for non-Mr. X clients in their merged TState. */
export const MRX_STATION_HIDDEN = -1

// --- Subscriptions --------------------------------------------------------

/**
 * Subscribe to `rooms/{code}/private/mrx`. Used only by the Mr. X client.
 * Detective clients should never call this. Returns the doc body (`{ station }`)
 * or `null` if the doc doesn't exist yet (e.g. game still in lobby).
 */
export function subscribePrivateMrx(
  code: string,
  callback: ( mrxDoc: MrxPrivateDoc | null ) => void,
): Unsubscribe {
  const ref = doc( db, ...dbdRoomPrivate( code, MRX_KEY ) )
  return onSnapshot( ref, ( snap ) => {
    if ( !snap.exists() ) {
      callback( null )
      return
    }
    callback( snap.data() as MrxPrivateDoc )
  } )
}

/**
 * Stream the merged player-view state for a single client. Combines:
 *  - the public game state at `rooms/{code}/state/main`,
 *  - and (only if `uid` is Mr. X) the private subdoc.
 *
 * The callback fires whenever either doc changes. Until the public doc has
 * loaded the callback is not invoked (avoids racing PlayerGameView).
 *
 * Mr. X identity check is by uid against `public.mrx.uid`. We re-evaluate on
 * every public-state snapshot so that if (for some reason) the room's Mr. X
 * changes mid-game, the subscription rewires itself.
 */
export function subscribePlayerState(
  _firestore: Firestore,
  code: string,
  uid: string,
  callback: ( state: ScotlandYardState | null ) => void,
): Unsubscribe {
  let lastPublic: GameState | null = null
  let lastMrxStation: number | null = null
  let mrxUnsub: Unsubscribe | null = null
  let isMrxNow = false

  function emit() {
    if ( lastPublic === null ) {
      callback( null )
      return
    }
    const station = isMrxNow
      ? ( lastMrxStation ?? MRX_STATION_HIDDEN )
      : MRX_STATION_HIDDEN
    callback( { public: lastPublic, mrxStation: station } )
  }

  const publicRef = doc( db, ...dbdRoomState( code ) )
  const publicUnsub = onSnapshot( publicRef, ( snap ) => {
    if ( !snap.exists() ) {
      lastPublic = null
      // Drop any private subscription too.
      if ( mrxUnsub ) {
        mrxUnsub()
        mrxUnsub = null
      }
      isMrxNow = false
      lastMrxStation = null
      callback( null )
      return
    }
    const next = snap.data() as GameState
    lastPublic = next
    const wasMrx = isMrxNow
    isMrxNow = next.mrx.uid === uid
    if ( isMrxNow && !wasMrx ) {
      // Wire up the Mr. X private subscription.
      mrxUnsub = subscribePrivateMrx( code, ( priv ) => {
        lastMrxStation = priv ? priv.station : null
        emit()
      } )
    } else if ( !isMrxNow && wasMrx ) {
      if ( mrxUnsub ) {
        mrxUnsub()
        mrxUnsub = null
      }
      lastMrxStation = null
    }
    emit()
  } )

  return () => {
    publicUnsub()
    if ( mrxUnsub ) mrxUnsub()
  }
}

// --- Move application -----------------------------------------------------

export interface ApplyMoveOpts {
  code: string
  move: Move
}

/**
 * Apply a move to the live game in Firestore. Atomic across the public state
 * doc, Mr. X's private doc, and (on game-end) the room doc.
 *
 * Errors:
 *  - `RoomNotInGameError` — `state/main` doesn't exist.
 *  - `GameNotActiveError` — the game has already ended.
 *  - `MrxStateMissingError` — `private/mrx` doesn't exist.
 *  - `IllegalMoveError` — engine rejected the move.
 */
export async function applyMove( opts: ApplyMoveOpts ): Promise<void> {
  const { code, move } = opts

  const stateRef = doc( db, ...dbdRoomState( code ) )
  const mrxRef = doc( db, ...dbdRoomPrivate( code, MRX_KEY ) )
  const roomRef = doc( db, ...dbdRoom( code ) )

  await runTransaction( db, async ( tx ) => {
    const [ stateSnap, mrxSnap ] = await Promise.all( [
      tx.get( stateRef ),
      tx.get( mrxRef ),
    ] )

    if ( !stateSnap.exists() ) throw new RoomNotInGameError( code )
    const publicState = stateSnap.data() as GameState
    if ( publicState.status !== 'active' ) {
      throw new GameNotActiveError( code, publicState.status )
    }
    if ( !mrxSnap.exists() ) throw new MrxStateMissingError( code )
    const mrxPriv = mrxSnap.data() as MrxPrivateDoc

    // Backfill stationHistory for older games written before the field
    // existed: assume the current station is the first (and only known)
    // entry. Every subsequent move will then track properly.
    const priorMrxHistory = mrxPriv.stationHistory ?? [ mrxPriv.station ]

    let result
    try {
      result = engineApplyMove( {
        state: publicState,
        move,
        mrxStation: mrxPriv.station,
        mrxStationHistory: priorMrxHistory,
        adjacency: defaultAdjacency(),
      } )
    } catch ( err ) {
      if ( err instanceof EngineError ) {
        throw new IllegalMoveError( err.code, err.message )
      }
      throw err
    }

    let nextPublic = result.state
    const nextMrxStation = result.mrxStation
    const nextMrxHistory = result.mrxStationHistory

    // On game-end, denormalize the full per-pawn replay payload onto the
    // public state doc. This is the *only* time Mr. X's full path is
    // written publicly; while the game is active the history lives in the
    // private subdoc. Once `state.public.replay` is set, all clients can
    // render the post-game replay UI from public state alone.
    if ( nextPublic.winner !== null ) {
      const detectiveStationsByUid: Record<string, number[]> = {}
      for ( const det of nextPublic.detectives ) {
        // Backfill with `[station]` if a detective somehow lacks history
        // (e.g. game started before the field existed and is finishing now).
        detectiveStationsByUid[ det.uid ] =
          det.stationHistory ?? ( det.station != null ? [ det.station ] : [] )
      }
      nextPublic = {
        ...nextPublic,
        replay: {
          mrxStations: nextMrxHistory,
          detectiveStationsByUid,
        },
      }
    }

    tx.set( stateRef, nextPublic as unknown as DocumentData )
    tx.set( mrxRef, {
      station: nextMrxStation,
      stationHistory: nextMrxHistory,
    } satisfies MrxPrivateDoc )

    // If the engine reports a winner, also flip the room status to
    // 'finished' in the same transaction. The platform's room doc owns the
    // top-level status flag (the public state's own `status` is also set to
    // 'detectives_won' / 'mrx_won' by the engine; both are sources of truth
    // but the room doc is what the shells switch on).
    if ( nextPublic.winner !== null ) {
      tx.update( roomRef, {
        status: 'finished',
        finishedAtMs: Date.now(),
        winner: nextPublic.winner,
      } )
    }
  } )
}
